// src/logic/trigger-matcher.ts
// (V9 Patch 3: Why-Role lexical suppression; safer syn-substring (>=2 toks);
//  head-anchor bonus ignores generic anchors; keep all prior hygiene.)

import { matcherConfig } from "./matcher.config";
import { semanticBackstopMatch } from "./semantic/semantic-matcher"; // Stage 6 backstop
import { semanticConfig } from "../config/semantic.config"; // debug guard only

// --- Types ---
export type CueNote = {
  id: string;
  title: string;
  triggers: string[];
  body_markdown: string;
};

type RunOpts = { preferNewest?: boolean };

export type MatchCandidate = {
  id: string;
  title: string;
  trigger: string;
  score: number;
  stage: "strict" | "substring" | "tail" | "anchor" | "fuzzy" | "semantic";
};

// --- Debug & Cooldown ---
const DEBUG = false;
function dbg(...args: any[]) { if (DEBUG) console.log("[MATCHER]", ...args); }

class CooldownManager {
  private now() { return Date.now(); }
  private getCooldownMs(): number {
    try {
      const v = localStorage.getItem("cb_cooldown_ms");
      const n = v ? parseInt(v, 10) : NaN;
      return Number.isFinite(n) ? n : matcherConfig.cooldownMsDefault;
    } catch {
      return matcherConfig.cooldownMsDefault;
    }
  }
  private getFiredMap(): Record<string, number> {
    try { return JSON.parse(localStorage.getItem("cb_fired") || "{}"); }
    catch { return {}; }
  }
  stampFired(id: string) {
    try {
      const m = this.getFiredMap();
      m[id] = this.now();
      localStorage.setItem("cb_fired", JSON.stringify(m));
    } catch {}
  }
  isCooling(id: string): boolean {
    const m = this.getFiredMap();
    const t = m[id];
    if (!t) return false;
    return (this.now() - t) < this.getCooldownMs();
  }
}
const cooldownManager = new CooldownManager();

// --- Normalization & Text Processing ---
const PUNCT_RE = /[^\p{L}\p{N}\s']/gu;
const MULTISPACE_RE = /\s+/g;

function stripChannelTag(s: string): string {
  return s.replace(/^\((system|mic)\)\s*/i, "");
}

function normalize_base(s: string): string {
  let t = stripChannelTag(s);
  t = t.toLowerCase();
  t = t.normalize("NFKC");
  t = t.replace(/\bwhy\s*s\b/g, "why us").replace(/\bwhy\s+ys\b/g, "why us");
  t = t.replace(PUNCT_RE, " ");
  t = t.replace(MULTISPACE_RE, " ").trim();
  return t;
}

export function normalize(s: string): string { return normalize_base(s); }

function tokenize(s: string): string[] { return s.split(/\s+/).filter(Boolean); }
function removeStops(tokens: string[]): string[] {
  return tokens.filter(t => !matcherConfig.stopwords.has(t));
}
function applyMicroSynonyms(tokens: string[]): string[] {
  return tokens.map(t => matcherConfig.microSynonyms[t] ?? t);
}
function synString(s: string): string {
  const toks = applyMicroSynonyms(removeStops(tokenize(s)));
  return toks.join(" ");
}

const _fillerParts = Array.from(matcherConfig.fillerShortSet).concat(matcherConfig.fillerPhrases);
const FILLER_REGEX = new RegExp(
  `\\b(?:${_fillerParts.map(s => s.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\$&")).join("|")})\\b`,
  "gi"
);
function stripFillers(s: string): string {
  return s.replace(FILLER_REGEX, " ").replace(MULTISPACE_RE, " ").trim();
}
function isFillerOnly(text: string): boolean { return stripFillers(text) === ""; }

// --- Helper Functions ---
function nonSpaceLen(s: string): number { return s.replace(/\s+/g, "").length; }
function nonEmpty(s: string): boolean { return !!(s && s.trim().length); }
function trimGenericTail(toks: string[]): string[] {
  if (toks.length <= 1) return toks;
  const last = toks[toks.length - 1];
  return matcherConfig.genericTails.has(last) ? toks.slice(0, -1) : toks;
}
function isKeepAlignFamily(tokens: string[]): boolean {
  const keepIdx = tokens.indexOf("keep");
  if (keepIdx === -1) return false;
  const t1 = tokens[keepIdx + 1];
  const t2 = tokens[keepIdx + 2];
  if (t1 === "align") return true;
  return !!(t1 && matcherConfig.gapNounAllow.has(t1) && t2 === "align");
}
function hasContentOverlap(a: string[], b: string[]): boolean {
  if (!a.length || !b.length) return false;
  const setA = new Set(a);
  for (const x of b) if (setA.has(x)) return true;
  return false;
}
// Meaningful overlap for Fuzzy
function hasMeaningfulContentOverlap(a: string[], b: string[]): boolean {
  if (!a.length || !b.length) return false;
  const setB = new Set(b);
  for (const tok of a) {
    if (!setB.has(tok)) continue;
    if (matcherConfig.fuzzyOverlapExclusions.has(tok)) continue;
    if (matcherConfig.highSignalAnchors.has(tok)) return true;
    if (tok.length >= 4) return true;
  }
  return false;
}
function simpleSimilarity(a: string, b: string): number {
  const getTrigrams = (s: string) => {
    const set = new Set<string>();
    const str = s.replace(/\s+/g, " ");
    for (let i = 0; i < str.length - 2; i++) set.add(str.slice(i, i + 3));
    return set;
  };
  const A = getTrigrams(a), B = getTrigrams(b);
  if (A.size === 0 && B.size === 0) return 1;
  let inter = 0; A.forEach(x => { if (B.has(x)) inter++; });
  const union = A.size + B.size - inter;
  return union === 0 ? 0 : inter / union;
}
function isLowSignalFragment(text: string, tokens: string[]): boolean {
  const ns = nonSpaceLen(text);
  const tokLen = tokens.length;
  const hasAnchor = tokens.some(t => matcherConfig.highSignalAnchors.has(t));
  if (matcherConfig.denyFragments.has(text)) return true;
  if (tokLen === 1) {
    const t = tokens[0];
    if (matcherConfig.singleTokenWhitelist.has(t)) return false;
    if (t.length <= 2 || t === "tell" || t === "there" || t === "do") return true;
  }
  return tokLen < 3 && ns < 12 && !hasAnchor;
}
function isRoadblockNote(note: CueNote): boolean {
  const t = ((note.title || "") + " " + (note.id || "")).toLowerCase();
  return t.includes("roadblock") || t.includes("went wrong");
}
function hasRoadblockSignal(text: string): boolean {
  return /\b(went wrong|roadblock|roadblocks|challenges?|challenged|blockers?|mistakes?|issues?|problems?)\b/.test(text); // NEW: add roadblock(s)
}

// Stopword-elastic matcher: trig tokens must appear in order; gaps in text may only be stopwords.
function elasticStopwordIncludes(textTokens: string[], trigTokens: string[]): boolean {
  let i = 0;
  for (const tt of trigTokens) {
    while (i < textTokens.length && textTokens[i] !== tt) {
      if (!matcherConfig.stopwords.has(textTokens[i])) return false;
      i++;
    }
    if (i === textTokens.length) return false;
    i++;
  }
  return true;
}

// NEW: Why-Role lexical suppression (global)
const WHY_SIG_RE = /\b(why|stand\s+out|right\s+next\s+step|this\s+particular\s+role|attracted|appealed|resonated|why\s+us|why\s+this\s+company|choose\s+us|applied\s+here)\b/i;
const GENERIC_ROLE_TOK_RE = /\b(role|roles|team|teams|company)\b/i;
// Generic anchors excluded from head-anchor bonus
const GENERIC_ANCHOR = new Set(["role","company"]); // NEW

// --- Core Matcher Logic ---
export function runMatcher(
  textRaw: string,
  notes: CueNote[],
  activeId?: string | null,
  opts?: RunOpts
): MatchCandidate | null {
  const text = normalize_base(textRaw);
  if (!text || isFillerOnly(text)) return null;

  const toksRaw = tokenize(text);
  if (isLowSignalFragment(text, toksRaw)) { dbg("low-signal-fragment", text); return null; }

  const textSyn = synString(text);
  const inputTokens = applyMicroSynonyms(toksRaw);
  const inputNoStop = removeStops(inputTokens);
  const inputCore = trimGenericTail(removeStops(inputTokens)).join(" ");
  const preferNewest = !!(opts && opts.preferNewest);
  const allowFuzzy = (inputTokens.length >= 3) && (nonSpaceLen(text) >= 10);
  const FUZZY_THRESHOLD = inputTokens.length < 4 ? 0.50 : 0.42;
  const inputKeepAlign = isKeepAlignFamily(inputTokens);
  const inputAnchors = inputNoStop.filter(t => matcherConfig.highSignalAnchors.has(t));

  const all: MatchCandidate[] = [];

  for (const note of notes) {
    if (cooldownManager.isCooling(note.id)) { dbg("cooldown", note.id); continue; }

    const bias = (note.id === activeId ? 0.01 : 0) + (preferNewest ? 0.001 : 0);
    const roadblockNote = isRoadblockNote(note);
    const roadblockSignal = hasRoadblockSignal(text);
    const shortInput = inputTokens.length < 6;
    const blockRoadblockLoose = roadblockNote && shortInput && !roadblockSignal;

    const noteTitleNorm = (note.title || "").toLowerCase();
    const isWhyRoleNote =
      /\bwhy this role\b/.test(noteTitleNorm) ||
      /\bwhy this company\b/.test(noteTitleNorm) ||
      /\bwhy us\b/.test(noteTitleNorm);

    for (const trigRaw of note.triggers) {
      const trig = normalize_base(trigRaw);
      const trigSyn = synString(trig);
      const trigSynUsable = nonEmpty(trigSyn);
      const textSynUsable = nonEmpty(textSyn);

      // NEW: Suppress lexical stages for Why-Role unless utterance has a "why" signal.
      if (isWhyRoleNote) {
        const hasWhySignal = WHY_SIG_RE.test(text);
        if (!hasWhySignal && GENERIC_ROLE_TOK_RE.test(text)) {
          // Skip lexical (stages 1–5) so semantic can arbitrate.
          continue;
        }
      }

      // Stage 1: Strict Match
      if (text === trig || (trigSynUsable && textSynUsable && textSyn === trigSyn)) {
        all.push({ id: note.id, title: note.title, trigger: trigRaw, score: Math.min(1, 1 + bias), stage: "strict" });
        continue;
      }

      // Stage 2: Substring Match (+ stopword-elastic)
      // Safer syn-substring: require >=2 tokens on BOTH sides so "on track" ≠ "track"
      const trigSynToks = trigSyn.split(/\s+/).filter(Boolean);
      const textSynToks = textSyn.split(/\s+/).filter(Boolean);

      const rawSub = text.includes(trig) || trig.includes(text);
      const synSub =
        trigSynUsable && textSynUsable &&
        trigSynToks.length >= 2 && textSynToks.length >= 2 &&
        (textSyn.includes(trigSyn) || trigSyn.includes(textSyn));

      // Elastic (order-preserving, gaps can only be stopwords)
      let elasticOk = false;
      if (!rawSub && !synSub) {
        const textTokElastic = applyMicroSynonyms(tokenize(text)); // keep stops to test gaps
        const trigTokElastic = applyMicroSynonyms(tokenize(trig));
        if (trigTokElastic.length >= 2) {
          elasticOk = elasticStopwordIncludes(textTokElastic, trigTokElastic);
        }
      }

      const substringHit = rawSub || synSub || elasticOk;
      if (substringHit) {
        if (toksRaw.length >= 3 && nonSpaceLen(text) >= 12 && !/^is\s+your\s+biggest\b/.test(text)) {
          if (!blockRoadblockLoose) {
            const base = Math.max(trig.length, text.length);
            let sim = (trig.length / base) * 0.92;

            // Head-noun bonus for shared high-signal anchors
            // NEW: exclude generic anchors (role/company) from this bonus
            const trigTokensForBonus = removeStops(applyMicroSynonyms(tokenize(trig)));
            const sharesHeadAnchor = inputAnchors.some(a => !GENERIC_ANCHOR.has(a) && trigTokensForBonus.includes(a));
            if (sharesHeadAnchor) sim += matcherConfig.substringHeadAnchorBonus;

            all.push({ id: note.id, title: note.title, trigger: trigRaw, score: Math.min(0.98, sim + bias), stage: "substring" });
            continue;
          }
        }
      }

      // Stage 3: Tail Match
      const trigTokens = applyMicroSynonyms(tokenize(trig));
      const trigNoStop = removeStops(trigTokens);
      const trigCore = trimGenericTail(trigNoStop).join(" ");
      const trigCoreToks = trigCore ? trigCore.split(/\s+/).filter(Boolean) : [];
      const inputCoreToks = inputCore ? inputCore.split(/\s+/).filter(Boolean) : [];
      const tailCoreOk = (trigCoreToks.length >= 2) && (inputCoreToks.length >= 2);
      const trigKeepAlign = isKeepAlignFamily(trigTokens);
      if (tailCoreOk && ((inputKeepAlign && trigKeepAlign) || (trigCore && inputCore && (inputCore.includes(trigCore) || trigCore.includes(inputCore))))) {
        if (blockRoadblockLoose) continue;
        all.push({ id: note.id, title: note.title, trigger: trigRaw, score: Math.min(0.95, 0.88 + bias), stage: "tail" });
        continue;
      }

      // Stage 4: Anchor Match (tightened + relax when ≥2 shared anchors)
      const inputAnchorTokens = inputAnchors;
      if (inputAnchorTokens.length) {
        const trigNoStopSyn = removeStops(applyMicroSynonyms(tokenize(trig)));
        const shared = inputAnchorTokens.filter(a => trigNoStopSyn.includes(a));
        if (shared.length > 0) {
          const sim = simpleSimilarity(textSyn, trigSyn);
          const pass = sim >= 0.45 || shared.length >= 2; // relax if we share 2+ anchors
          if (pass) {
            if (blockRoadblockLoose) continue;
            all.push({ id: note.id, title: note.title, trigger: trigRaw, score: Math.min(0.92, sim + 0.02 + bias), stage: "anchor" });
            continue;
          }
        }
      }

      // Stage 5: Fuzzy Match (hygiene on overlap)
      if (allowFuzzy && trigSynUsable && textSynUsable) {
        const sim = simpleSimilarity(textSyn, trigSyn);
        const trigNoStopSyn2 = removeStops(applyMicroSynonyms(tokenize(trig)));
        if (sim >= FUZZY_THRESHOLD && hasMeaningfulContentOverlap(inputNoStop, trigNoStopSyn2)) {
          if (blockRoadblockLoose) continue;
          all.push({ id: note.id, title: note.title, trigger: trigRaw, score: Math.min(0.9, sim + bias), stage: "fuzzy" });
        }
      }
    }
  }

  // Stage 6: Semantic Backstop
  if (!all.length) {
    if (semanticConfig.enabled && (typeof console !== "undefined")) {
      try { console.info("[MATCHER] (semantic:start)", { textRaw }); } catch {}
    }
    const sem = semanticBackstopMatch(textRaw, notes);
    if (sem) {
      const best: MatchCandidate = {
        id: sem.id,
        title: sem.title,
        trigger: sem.trigger,
        score: Math.min(0.89, sem.score),
        stage: "semantic",
      };
      cooldownManager.stampFired(best.id);
      if (semanticConfig.enabled && (typeof console !== "undefined")) {
        try { console.info(`[MATCHER] (semantic:result) ${best.title} via "${best.trigger}" score=${best.score.toFixed(2)}`); } catch {}
      }
      return best;
    }
    dbg("no-match"); return null;
  }

  // --- StageGate: enforce precedence before scoring across candidates ---
  const precedence: Array<MatchCandidate["stage"]> = ["strict", "substring", "tail", "anchor", "fuzzy"];
  const present = new Set(all.map(c => c.stage));
  const highest = precedence.find(st => present.has(st));
  const pool = highest ? all.filter(c => c.stage === highest) : all;

  // Rank and finalize within the gated pool
  pool.sort((a, b) => b.score - a.score);
  let best = pool[0];

  // Sticky active card nudge (same stage)
  if (activeId) {
    const topScore = best.score;
    const activeCand = pool.find(c => c.id === activeId && (topScore - c.score) <= 0.01);
    if (activeCand) best = activeCand;
  }

  // Keyword nudges (respect StageGate; search pool only)
  for (const nudge of matcherConfig.keywordNudges) {
    for (const pattern of nudge.patterns) {
      if (pattern.test(text)) {
        const nudgedCandidate = pool.find(c => c.id === nudge.noteId);
        if (nudgedCandidate) { best = nudgedCandidate; break; }
      }
    }
  }

  cooldownManager.stampFired(best.id);

  if (DEBUG) {
    const byStage = pool.reduce<Record<string, number>>((m, c) => ((m[c.stage] = (m[c.stage] || 0) + 1), m), {});
    console.log("[MATCHER] StageGate", {
      stages_present: Array.from(present.values()),
      gated_stage: highest,
      kept_counts_by_stage: byStage,
      chosen: { id: best.id, title: best.title, stage: best.stage, score: best.score }
    });
  }

  dbg("BEST", best);
  return best;
}

// --- Public API ---
export function buildMatcherFromNotes(notes: CueNote[]) {
  return {
    match: (textRaw: string, activeId?: string | null, opts?: RunOpts) =>
      runMatcher(textRaw, notes, activeId, opts),
  };
}
